import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  accent: '#4FC3F7',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3'
};

// Fonction pour le label et couleur du statut
const getStatusStyle = (status) => {
  switch (status?.toLowerCase()) {
    case 'envoye':
    case 'envoyé':
      return { 
        text: "Envoyé", 
        icon: "bi-check-circle",
        color: COLORS.success,
        bgColor: '#e8f5e9'
      };
    case 'erreur':
    case 'error':
      return { 
        text: "Erreur", 
        icon: "bi-exclamation-triangle",
        color: COLORS.danger,
        bgColor: '#ffebee'
      };
    default:
      return { 
        text: "Inconnu", 
        icon: "bi-question-circle",
        color: COLORS.darkText,
        bgColor: '#f5f5f5'
      };
  }
};

const HistoriqueRappels = () => {
  const navigate = useNavigate();
  const [rappels, setRappels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  
  // Récupérer les infos utilisateur
  const [userInfo, setUserInfo] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Filtres simples
  const [filters, setFilters] = useState({
    date_debut: "",
    date_fin: "",
    status: ""
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });

  // Statistiques
  const [stats, setStats] = useState({
    total_rappels: 0,
    envoyes: 0,
    erreurs: 0,
    dernier_rappel: null
  });

  // Permission
  const userProfile = userInfo ? String(userInfo.profile || userInfo.poste || "") : "";
  const canAccess = userProfile === "3"; // Seuls les gestionnaires

  const fetchHistorique = useCallback(async (page = 1) => {
    if (!userInfo) return;
    
    setLoading(true);
    setServerError("");
    
    const token = localStorage.getItem("access_token");
    if (!token) {
      setServerError("Token d'authentification manquant.");
      setLoading(false);
      setTimeout(() => navigate("/login"), 2000);
      return;
    }
    
    try {
      const params = new URLSearchParams();
      
      // Ajouter les filtres seulement s'ils sont définis
      if (filters.date_debut) params.append("date_debut", filters.date_debut);
      if (filters.date_fin) params.append("date_fin", filters.date_fin);
      if (filters.status) params.append("status", filters.status);
      
      params.append("page", page);
      params.append("limit", pagination.limit);
      
      const url = `${API_BASE_URL}/clients/get_rappels.php?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      if (response.data?.success) {
        setRappels(response.data.data || []);
        setStats(response.data.stats || {
          total_rappels: 0,
          envoyes: 0,
          erreurs: 0,
          dernier_rappel: null
        });
        
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
        
        if (response.data.data?.length === 0) {
          setInfoMsg("Aucun rappel trouvé avec les filtres actuels.");
        } else {
          setInfoMsg("");
        }
      } else {
        setServerError(response.data?.message || "Erreur de chargement.");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setServerError("Accès interdit. Seuls les gestionnaires peuvent accéder à l'historique.");
      } else if (err.response?.status === 400) {
        setServerError(err.response.data?.message || "Erreur dans la requête.");
      } else if (!err.response) {
        setServerError("Serveur indisponible.");
      } else {
        setServerError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit, navigate, userInfo]);

  useEffect(() => {
    if (userInfo && userInfo.societe_id) {
      fetchHistorique();
    }
  }, [fetchHistorique, userInfo]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchHistorique(1);
  };

  const clearFilters = () => {
    setFilters({
      date_debut: "",
      date_fin: "",
      status: ""
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchHistorique(1);
    setShowFilters(false);
  };

  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchHistorique(1);
    setShowFilters(false);
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "0000-00-00" || dateString === "0000-00-00 00:00:00") {
      return "-";
    }
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR');
    } catch {
      return dateString;
    }
  };

  // Si pas d'utilisateur
  if (!userInfo) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Historique des Rappels</h1>
        </div>
        <section className="section">
          <div className="text-center py-5">
            <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des informations...</p>
          </div>
        </section>
      </Layout>
    );
  }

  // Si pas accès
  if (!canAccess) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Historique des Rappels</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Historique Rappels</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="text-center py-5">
            <div className="mb-4">
              <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                width: '80px',
                height: '80px',
                backgroundColor: '#F5F7FA',
                color: '#B0BEC5'
              }}>
                <i className="bi bi-lock fs-1"></i>
              </div>
            </div>
            <h5 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Accès restreint</h5>
            <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
              Seuls les gestionnaires peuvent accéder à l'historique des rappels.
            </p>
            <button
              className="btn fw-semibold mt-4"
              onClick={() => navigate("/dashboard")}
              style={{
                color: COLORS.primary,
                borderColor: COLORS.primary,
                backgroundColor: 'transparent',
                borderRadius: '8px',
                padding: '10px 24px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = COLORS.primary;
                e.target.style.color = COLORS.white;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = COLORS.primary;
              }}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Retour au tableau de bord
            </button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Historique des Rappels</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item">Gestion</li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Historique Rappels</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="card-title mb-1" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  width: '100%'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-envelope-check"></i>
                    </div>
                    Historique des rappels envoyés
                    <span className="badge ms-3" style={{ 
                      backgroundColor: COLORS.secondary,
                      color: COLORS.white,
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '0.8rem'
                    }}>
                      {rappels.length} trouvé(s)
                    </span>
                  </div>
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Consultez l'historique des rappels de fin de contrat envoyés aux clients de votre société
                </p>
              </div>
              
              <div className="d-flex gap-2">
                {/* Bouton Filtres */}
                <button
                  className="btn fw-semibold"
                  onClick={() => setShowFilters(!showFilters)}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#E3F2FD';
                    e.target.style.borderColor = COLORS.secondary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = COLORS.primary;
                  }}
                >
                  <i className="bi bi-funnel me-2"></i>
                  Filtres
                  {(filters.date_debut || filters.date_fin || filters.status) ? (
                    <span className="badge fw-semibold ms-1" style={{ 
                      backgroundColor: COLORS.danger,
                      color: 'white',
                      fontSize: '0.7rem'
                    }}>!</span>
                  ) : null}
                </button>
                
                {/* Bouton Rafraîchir */}
                <button
                  onClick={() => fetchHistorique(pagination.page)}
                  className="btn fw-semibold"
                  disabled={loading}
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.success,
                    borderColor: COLORS.success,
                    borderRadius: '8px',
                    padding: '10px 20px',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  ) : (
                    <i className="bi bi-arrow-clockwise me-2"></i>
                  )}
                  Rafraîchir
                </button>
              </div>
            </div>

            {/* Cartes de statistiques */}
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card" style={{
                  borderRadius: '10px',
                  backgroundColor: COLORS.primary,
                  color: 'white',
                  border: 'none',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="number" style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                          {stats.total_rappels}
                        </div>
                        <div className="label" style={{ fontSize: '0.9rem', opacity: '0.9' }}>
                          Total Rappels
                        </div>
                      </div>
                      <i className="bi bi-envelope fs-2 opacity-75"></i>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-3">
                <div className="card" style={{
                  borderRadius: '10px',
                  backgroundColor: COLORS.success,
                  color: 'white',
                  border: 'none',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="number" style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                          {stats.envoyes}
                        </div>
                        <div className="label" style={{ fontSize: '0.9rem', opacity: '0.9' }}>
                          Envoyés avec succès
                        </div>
                      </div>
                      <i className="bi bi-check-circle fs-2 opacity-75"></i>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-3">
                <div className="card" style={{
                  borderRadius: '10px',
                  backgroundColor: COLORS.danger,
                  color: 'white',
                  border: 'none',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="number" style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                          {stats.erreurs}
                        </div>
                        <div className="label" style={{ fontSize: '0.9rem', opacity: '0.9' }}>
                          En erreur
                        </div>
                      </div>
                      <i className="bi bi-exclamation-triangle fs-2 opacity-75"></i>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-3">
                <div className="card" style={{
                  borderRadius: '10px',
                  backgroundColor: COLORS.warning,
                  color: COLORS.darkText,
                  border: 'none',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="label" style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                          Dernier rappel
                        </div>
                        <div className="number" style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                          {stats.dernier_rappel ? formatDate(stats.dernier_rappel) : 'Aucun'}
                        </div>
                      </div>
                      <i className="bi bi-clock fs-2 opacity-75"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filtres avancés */}
            {showFilters && (
              <div className="card mb-4" style={{ 
                backgroundColor: '#E3F2FD',
                border: 'none',
                borderRadius: '8px'
              }}>
                <div className="card-body py-3">
                  <h6 className="card-title mb-3" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '28px',
                      height: '28px',
                      backgroundColor: COLORS.white,
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-funnel"></i>
                    </div>
                    Filtres de recherche
                  </h6>
                  
                  <form onSubmit={handleSearch} className="row g-3">
                    {/* Dates */}
                    <div className="col-md-4">
                      <label className="form-label small" style={{ color: COLORS.primary, fontWeight: '500' }}>
                        Date début
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.date_debut}
                        onChange={(e) => handleFilterChange("date_debut", e.target.value)}
                        style={{
                          borderColor: '#e0e0e0',
                          color: COLORS.darkText
                        }}
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label small" style={{ color: COLORS.primary, fontWeight: '500' }}>
                        Date fin
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.date_fin}
                        onChange={(e) => handleFilterChange("date_fin", e.target.value)}
                        style={{
                          borderColor: '#e0e0e0',
                          color: COLORS.darkText
                        }}
                      />
                    </div>

                    {/* Statut */}
                    <div className="col-md-4">
                      <label className="form-label small" style={{ color: COLORS.primary, fontWeight: '500' }}>
                        Statut
                      </label>
                      <select
                        className="form-select"
                        value={filters.status}
                        onChange={(e) => handleFilterChange("status", e.target.value)}
                        style={{
                          borderColor: '#e0e0e0',
                          color: COLORS.darkText
                        }}
                      >
                        <option value="">Tous les statuts</option>
                        <option value="envoye">Envoyé</option>
                        <option value="erreur">Erreur</option>
                      </select>
                    </div>

                    {/* Boutons d'action */}
                    <div className="col-md-12">
                      <div className="d-flex justify-content-end gap-2 pt-2">
                        <button
                          type="button"
                          className="btn fw-semibold"
                          onClick={clearFilters}
                          style={{
                            color: COLORS.primary,
                            borderColor: COLORS.primary,
                            backgroundColor: 'transparent',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#E3F2FD';
                            e.target.style.borderColor = COLORS.secondary;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.borderColor = COLORS.primary;
                          }}
                        >
                          <i className="bi bi-x-lg me-1"></i> 
                          Effacer les filtres
                        </button>
                        
                        <button 
                          type="button" 
                          className="btn fw-semibold"
                          onClick={applyFilters}
                          style={{
                            backgroundColor: COLORS.primary,
                            borderColor: COLORS.primary,
                            color: COLORS.white,
                            borderRadius: '8px',
                            padding: '10px 20px',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = COLORS.secondary;
                            e.target.style.borderColor = COLORS.secondary;
                            e.target.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = COLORS.primary;
                            e.target.style.borderColor = COLORS.primary;
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          <i className="bi bi-check-lg me-2"></i> 
                          Appliquer les filtres
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {serverError && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                  ></button>
                </div>
              </div>
            )}
            
            {infoMsg && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.success}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2"></i>
                  <div className="flex-grow-1">
                    {infoMsg.split('\n').map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </div>
                  <button type="button" className="btn-close" onClick={() => setInfoMsg("")}></button>
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement de l'historique...</p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderBottom: `2px solid ${COLORS.accent}`
                      }}>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Client</th>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Date envoi</th>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Jours restants</th>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Date fin contrat</th>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Statut</th>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rappels.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-5">
                            <div className="mb-4">
                              <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                                width: '80px',
                                height: '80px',
                                backgroundColor: '#F5F7FA',
                                color: '#B0BEC5'
                              }}>
                                <i className="bi bi-envelope-x fs-1"></i>
                              </div>
                            </div>
                            <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun rappel trouvé</h6>
                            <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                              {filters.date_debut || filters.date_fin || filters.status 
                                ? "Aucun rappel ne correspond à vos critères de recherche." 
                                : "Aucun rappel n'a été envoyé à vos clients."}
                            </p>
                            <button
                              onClick={() => fetchHistorique()}
                              className="btn fw-semibold mt-3"
                              style={{
                                color: COLORS.primary,
                                borderColor: COLORS.primary,
                                backgroundColor: 'transparent',
                                borderRadius: '8px',
                                padding: '10px 24px',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = COLORS.primary;
                                e.target.style.color = COLORS.white;
                                e.target.style.transform = 'translateY(-2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = COLORS.primary;
                                e.target.style.transform = 'translateY(0)';
                              }}
                            >
                              <i className="bi bi-arrow-clockwise me-2"></i> 
                              Rafraîchir
                            </button>
                          </td>
                        </tr>
                      ) : (
                        rappels.map((rappel) => {
                          const statusStyle = getStatusStyle(rappel.status);

                          return (
                            <tr key={rappel.id_log} style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}>
                              <td style={{ padding: '16px' }}>
                                <div className="d-flex align-items-center">
                                  <div className="me-3">
                                    <div 
                                      className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                                      style={{ 
                                        width: '44px', 
                                        height: '44px', 
                                        fontSize: '15px',
                                        backgroundColor: COLORS.secondary,
                                        boxShadow: '0 2px 4px rgba(30, 136, 200, 0.1)'
                                      }}
                                    >
                                      {rappel.client_nom?.charAt(0) || 'C'}
                                    </div>
                                  </div>
                                  <div>
                                    <div style={{ 
                                      color: COLORS.primary, 
                                      fontWeight: '500',
                                      fontSize: '0.95rem'
                                    }}>
                                      {rappel.client_nom}
                                    </div>
                                    <div className="small" style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                                      {rappel.client_email}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                <div>
                                  <div style={{ 
                                    color: COLORS.darkText,
                                    fontWeight: '500',
                                    fontSize: '0.9rem'
                                  }}>
                                    {formatDate(rappel.date_envoi)}
                                  </div>
                                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                    {rappel.date_envoi_formate}
                                  </small>
                                </div>
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                <span className="badge fw-semibold" style={{ 
                                  backgroundColor: rappel.jours_restants > 30 ? '#e8f5e9' : 
                                                 rappel.jours_restants > 7 ? '#fff3e0' : '#ffebee',
                                  color: rappel.jours_restants > 30 ? '#2e7d32' : 
                                         rappel.jours_restants > 7 ? '#ef6c00' : '#c62828',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontSize: '0.8rem'
                                }}>
                                  <i className="bi bi-calendar-week me-1"></i>
                                  {rappel.jours_restants} jour(s)
                                </span>
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                <div className="small">
                                  <div className="d-flex align-items-center">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                      width: '16px',
                                      height: '16px',
                                      backgroundColor: '#E3F2FD',
                                      color: COLORS.secondary,
                                      fontSize: '0.6rem'
                                    }}>
                                      <i className="bi bi-calendar-check"></i>
                                    </div>
                                    <span style={{ color: COLORS.darkText }}>{formatDateOnly(rappel.date_fin_contrat)}</span>
                                  </div>
                                  <small className="text-muted" style={{ fontSize: '0.75rem', marginLeft: '24px' }}>
                                    {rappel.date_fin_formate}
                                  </small>
                                </div>
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                <span className="badge fw-semibold d-flex align-items-center" style={{ 
                                  backgroundColor: statusStyle.bgColor,
                                  color: statusStyle.color,
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontSize: '0.8rem',
                                  width: 'fit-content'
                                }}>
                                  <i className={`bi ${statusStyle.icon} me-1`}></i>
                                  {statusStyle.text}
                                </span>
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                <button
                                  onClick={() => navigate(`/clients/details/${rappel.id_client}`)}
                                  className="btn fw-semibold"
                                  title="Voir le client"
                                  style={{
                                    color: COLORS.primary,
                                    borderColor: COLORS.primary,
                                    backgroundColor: 'transparent',
                                    borderRadius: '6px',
                                    padding: '6px 12px',
                                    fontSize: '0.85rem',
                                    transition: 'all 0.3s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = COLORS.primary;
                                    e.target.style.color = COLORS.white;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.color = COLORS.primary;
                                  }}
                                >
                                  <i className="bi bi-person"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                  <nav className="mt-4">
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${!pagination.has_prev ? 'disabled' : ''}`}>
                        <button 
                          className="page-link fw-semibold" 
                          onClick={() => fetchHistorique(pagination.page - 1)}
                          disabled={!pagination.has_prev}
                          style={{
                            color: !pagination.has_prev ? '#B0BEC5' : COLORS.primary,
                            borderColor: '#e0e0e0',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            margin: '0 4px'
                          }}
                        >
                          <i className="bi bi-chevron-left me-1"></i> 
                          Précédent
                        </button>
                      </li>
                      
                      {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                        let pageNum;
                        if (pagination.total_pages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.total_pages - 2) {
                          pageNum = pagination.total_pages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }
                        
                        return (
                          <li key={pageNum} className="page-item">
                            <button 
                              className="page-link fw-semibold" 
                              onClick={() => fetchHistorique(pageNum)}
                              style={{
                                backgroundColor: pagination.page === pageNum ? COLORS.primary : 'transparent',
                                borderColor: pagination.page === pageNum ? COLORS.primary : '#e0e0e0',
                                color: pagination.page === pageNum ? COLORS.white : COLORS.primary,
                                padding: '8px 16px',
                                borderRadius: '6px',
                                margin: '0 4px',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (pagination.page !== pageNum) {
                                  e.target.style.backgroundColor = '#E3F2FD';
                                  e.target.style.borderColor = COLORS.secondary;
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (pagination.page !== pageNum) {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.borderColor = '#e0e0e0';
                                }
                              }}
                            >
                              {pageNum}
                            </button>
                          </li>
                        );
                      })}
                      
                      <li className={`page-item ${!pagination.has_next ? 'disabled' : ''}`}>
                        <button 
                          className="page-link fw-semibold" 
                          onClick={() => fetchHistorique(pagination.page + 1)}
                          disabled={!pagination.has_next}
                          style={{
                            color: !pagination.has_next ? '#B0BEC5' : COLORS.primary,
                            borderColor: '#e0e0e0',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            margin: '0 4px'
                          }}
                        >
                          Suivant 
                          <i className="bi bi-chevron-right ms-1"></i>
                        </button>
                      </li>
                    </ul>
                    <div className="text-center mt-3">
                      <small style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                        <span className="badge fw-semibold" style={{ 
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px',
                          margin: '0 8px'
                        }}>
                          Page {pagination.page} sur {pagination.total_pages}
                        </span>
                        <span className="mx-2">•</span>
                        <span className="badge fw-semibold" style={{ 
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px',
                          margin: '0 8px'
                        }}>
                          Total: {pagination.total} rappel(s)
                        </span>
                        <span className="mx-2">•</span>
                        <span className="badge fw-semibold" style={{ 
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px',
                          margin: '0 8px'
                        }}>
                          {rappels.length} sur cette page
                        </span>
                      </small>
                    </div>
                  </nav>
                )}
              </>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HistoriqueRappels;